import { useEffect, useState, useCallback, useRef } from "react";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { adminApi } from "services/api";
import { useSocket } from "context/SocketContext";

const STATUS_COLOR = {
  completed: "success",
  "in-progress": "info",
  ringing: "warning",
  voicemail: "warning",
  "no-answer": "error",
  failed: "error",
  busy: "error",
};

function VoicemailPlayButton({ callId }) {
  const [src, setSrc] = useState(null);
  const srcRef = useRef(null);
  const play = async () => {
    if (src) return;
    const blob = await adminApi.getCallVoicemail(callId);
    const objectUrl = URL.createObjectURL(blob);
    srcRef.current = objectUrl;
    setSrc(objectUrl);
  };
  useEffect(
    () => () => {
      if (srcRef.current) URL.revokeObjectURL(srcRef.current);
    },
    []
  );
  // maxWidth (not width:100%, which collapses inside the desktop table's max-content cell)
  // keeps the native 300px player on desktop but shrinks it to the phone card's value column.
  if (src) return <audio controls autoPlay src={src} style={{ height: 32, maxWidth: "100%" }} />;
  return (
    <IconButton
      size="small"
      color="info"
      onClick={play}
      aria-label="Play voicemail"
      sx={{ p: { xs: 1, md: "5px" } }}
    >
      <Icon fontSize="small">play_circle</Icon>
    </IconButton>
  );
}

function Calls() {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const socket = useSocket();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getCalls();
      setCalls(res.calls || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!socket) return undefined;
    const upsert = (call) =>
      setCalls((prev) => {
        const rest = prev.filter((c) => c._id !== call._id);
        return [call, ...rest].sort((a, b) => new Date(b.startedAt) - new Date(a.startedAt));
      });
    socket.on("call:new", upsert);
    socket.on("call:updated", upsert);
    return () => {
      socket.off("call:new", upsert);
      socket.off("call:updated", upsert);
    };
  }, [socket]);

  const columns = [
    { Header: "caller", accessor: "caller", width: "20%", align: "left" },
    { Header: "direction", accessor: "direction", width: "10%", align: "left" },
    {
      Header: "status",
      accessor: "status",
      width: "15%",
      align: "left",
      // The cell value stays the plain status text so the table search can match it.
      Cell: ({ value }) => (
        <Chip label={value} color={STATUS_COLOR[value] || "default"} size="small" />
      ), // eslint-disable-line react/prop-types
    },
    { Header: "duration", accessor: "duration", width: "10%", align: "left" },
    { Header: "when", accessor: "when", width: "20%", align: "left" },
    { Header: "voicemail", accessor: "voicemail", width: "10%", align: "left" },
  ];

  const rows = calls.map((c) => ({
    caller: c.displayName || c.customerPhone,
    direction: c.direction,
    status: c.status,
    duration: c.durationSeconds ? `${c.durationSeconds}s` : "—",
    when: new Date(c.startedAt).toLocaleString(),
    voicemail: c.voicemailUrl ? <VoicemailPlayButton callId={c._id} /> : "—",
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={3} pb={3}>
        <Card>
          <MDBox p={2}>
            <MDTypography variant="h6">Calls</MDTypography>
          </MDBox>
          <DataTable
            table={{ columns, rows }}
            isSorted={false}
            entriesPerPage={false}
            showTotalEntries={false}
            noEndBorder
            canSearch
          />
          {!loading && calls.length === 0 && (
            <MDBox p={3}>
              <MDTypography color="text.secondary">No calls yet.</MDTypography>
            </MDBox>
          )}
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}

export default Calls;
